"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { notifyPortfolioRefresh } from "@/lib/portfolio-refresh";
import PlaceTradeContainer from "./PlaceTrade/PlaceTradeContainer";
import { usePolymarketGeoblock } from "@/hooks/usePolymarketGeoblock";
import PolymarketGeoblockedNotice from "@/app/components/PolymarketGeoblockedNotice";
import { FiCheck, FiLoader, FiPlus, FiAlertCircle, FiCopy, FiLock } from "react-icons/fi";
import { ZapIcon } from "@/app/components/icons/ZapIcon";
import { GavelIcon } from "@/app/components/icons/GavelIcon";
import AnimatedPrice from "@/app/components/AnimatedPrice";
import SlippageWarning from "@/app/components/SlippageWarning";
import CdnImage from "@/components/CdnImage";
import dynamic from "next/dynamic";
const TradingModal = dynamic(() => import("@/app/components/TradingModal"), { ssr: false });
import { useAuth } from "@/hooks/useAuth";
import { useEnableTrading } from "@/hooks/useEnableTrading";
import { useSolanaUSDCBalance } from "@/hooks/useSolanaUSDCBalance";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useMarketPriceOptional } from "../context/MarketPriceContext";
import { authFetch } from "@/lib/authFetch";
import { useUserMe } from "@/contexts/UserMeContext";
import { getOutcomeAbbr } from "@/app/event/[slug]/components/types";
import { cdn } from "@/lib/cdn";
import { toast } from "sonner";
import { celebrateSimple, celebrateFromElement } from "@/lib/confetti";
import { reportDextoolsConversion } from "@/lib/dextools-tracking";
import TradeToast from "@/app/components/TradeToast";
import { usePoints } from "@/contexts/PointsContext";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import { useT } from "@/hooks/useT";
import { useDepositModalStore } from "@/stores/useDepositModalStore";
import { useBscDepositModalStore } from "@/stores/useBscDepositModalStore";
import { useFundWallet as useFundSolanaWallet } from "@privy-io/react-auth/solana";
import { useMarketRewardsParams } from "@/hooks/useMarketRewardsParams";
import SmartMoneyBadge from "@/app/components/SmartMoneyBadge";
import { useDFlowRealtimePrice } from "@/hooks/useDFlowRealtimePrice";
import { usePredictRealtimePrice } from "@/hooks/usePredictRealtimePrice";
import { useDFlowPositionsQuery } from "@/hooks/useDFlowPositionsQuery";
import { useKalshiExchangeStatus } from "@/hooks/useKalshiExchangeStatus";

// Detect interval (in seconds) from slug pattern: btc-updown-{5m|15m|1h|4h}-{epoch}
function getSeriesInterval(slug: string): number | null {
  const match = slug.match(/-(\d+)(m|h)-\d+$/);
  if (!match) return null;
  const val = parseInt(match[1]);
  return match[2] === 'h' ? val * 3600 : val * 60;
}

// Calculate the current live market slug by replacing the epoch
function getLiveMarketSlug(slug: string): string | null {
  const interval = getSeriesInterval(slug);
  if (!interval) return null;
  const now = Math.floor(Date.now() / 1000);
  const currentEpoch = Math.floor(now / interval) * interval;
  // Replace the epoch at the end of the slug
  const base = slug.replace(/-\d+$/, '');
  const liveSlug = `${base}-${currentEpoch}`;
  return liveSlug === slug ? null : liveSlug; // null if same market
}

/** Format price in cents respecting decimal precision (Predict.fun / Kalshi subpenny).
 *  Polymarket default: one decimal only when the value drifts off a
 *  whole cent — matches PM, which renders standard 0.01-tick markets
 *  as integers and only shows ".x" on fine-tick markets. */
function fmtCents(price: number, dp?: number): string {
  const cents = (price ?? 0) * 100;
  if (dp != null && dp > 0) return cents.toFixed(dp);
  const rounded = Math.round(cents);
  return Math.abs(cents - rounded) > 0.05 ? cents.toFixed(1) : rounded.toString();
}

/**
 * `outcomeIndex` is authoritative (Polymarket data-api). String matching
 * the label breaks for Over/Under, Up/Down, sports — see comment in
 * eventcard/helpers.ts for the canonical version.
 */
function getBinarySide(_outcomes: string[] | undefined, outcomeIndex: number): "YES" | "NO" {
  return outcomeIndex === 0 ? "YES" : "NO";
}

interface TradingPanelProps {
  marketData: {
    slug: string;
    question: string;
    outcomePrices: number[];
    outcomes: string[];
    clobTokenIds: string[];
    image?: string | null;
    endDate?: string | null;
    decimalPrecision?: number;
    // For multi-outcome events, the outcome name (e.g., "Cameron Young")
    groupItemTitle?: string | null;
  };
  isActive: boolean;
  source?: "polymarket" | "kalshi" | "predict" | "hyperliquid";
  // Kalshi-specific
  kalshiTicker?: string;
  dflowMints?: { yesMint?: string; noMint?: string } | null;
  // Predict-specific
  predictMarketId?: string;
  // Polymarket split/merge
  conditionId?: string;
  // Sports: event-level slug/title for outcome abbreviations
  eventSlug?: string;
  eventTitle?: string;
  // Notify parent when selected outcome changes (used by sports event page header)
  onOutcomeChange?: (idx: 0 | 1) => void;
  // Pre-select an outcome on mount (e.g., from query params) without opening trading modal
  initialOutcomeIndex?: 0 | 1 | null;
  /** Pre-select the buy/sell tab on mount — used when the modal opens from
   *  the "Sell" CTA in /portfolio so the user lands directly on SELL. */
  initialMode?: "buy" | "sell";
  /** Optional header row rendered inside the panel card, above "Place Trade".
   *  Used by the event page sidebar to show the selected sub-market's image
   *  + title + dropdown + alert/AI actions as part of the same visual unit,
   *  instead of a separate floating card on top. */
  contextHeader?: React.ReactNode;
  /** Hides Scenarios + LiveTradeTicker. Used on mobile bottom sheets so
   *  the CTA doesn't fall below the fold. */
  compact?: boolean;
  /** Post-author referral attribution. When present, the trade is credited
   *  to the post author (20% of net builder fee). Falls back to URL query
   *  params (`?via=&p=`) when omitted. */
  referralPostId?: string | null;
  referralAuthorId?: string | null;
  /** Pre-known position used to instant-render the SellContext. See
   *  `PlaceTradeContainer.seedOwnedPosition`. */
  seedOwnedPosition?: { outcome: "yes" | "no"; shares: number; avgPrice: number; currentPrice?: number; displayLabel?: string } | null;
}

export default function TradingPanel(props: TradingPanelProps) {
  const {
    marketData, isActive, source = "polymarket",
    kalshiTicker, dflowMints, predictMarketId,
    conditionId, onOutcomeChange, initialOutcomeIndex, initialMode, contextHeader,
    compact,
    referralPostId, referralAuthorId,
    seedOwnedPosition,
  } = props;
  const t = useT();

  // PlaceTrade V2 router. V2 now covers all three sources the legacy handled:
  //   polymarket  → /api/trade/execute|sell|limit-order, Safe wallet
  //   kalshi      → /api/dflow/execute, Solana sponsor wallet
  //   predict     → /api/trading/predict/execute, BSC EOA
  // Escape hatches:
  //   ?legacy=1                      → force the legacy 1880-line panel
  //   NEXT_PUBLIC_PLACETRADE_V2_OFF  → kill switch for full rollback
  const searchParamsForRouting = useSearchParams();
  const forceLegacy = searchParamsForRouting?.get("legacy") === "1";
  const envKillSwitch = process.env.NEXT_PUBLIC_PLACETRADE_V2_OFF === "1";
  const useV2 = !forceLegacy && !envKillSwitch;

  // 2026-05-26: Polymarket geoblock — replace the panel content with a
  // notice when the user is in a restricted region. Hook is
  // unconditional, but only consequential for source === "polymarket".
  // Kalshi/Predict pages render the same TradingPanel but skip this
  // branch because the source check gates it.
  const pmGeo = usePolymarketGeoblock();
  if (source === "polymarket" && pmGeo.blocked && !pmGeo.error) {
    const regionLabel = [pmGeo.country, pmGeo.region]
      .filter((s): s is string => !!s && s.length > 0)
      .join(" / ");
    return <PolymarketGeoblockedNotice regionLabel={regionLabel} />;
  }

  if (useV2) {
    return (
      <PlaceTradeContainer
        marketData={marketData}
        isActive={isActive}
        source={source}
        kalshiTicker={kalshiTicker}
        dflowMints={dflowMints}
        predictMarketId={predictMarketId}
        conditionId={conditionId}
        initialOutcomeIndex={initialOutcomeIndex}
        initialSide={initialMode}
        onOutcomeChange={onOutcomeChange}
        contextHeader={contextHeader}
        compact={compact}
        referralPostId={referralPostId}
        referralAuthorId={referralAuthorId}
        seedOwnedPosition={seedOwnedPosition}
      />
    );
  }

  return <TradingPanelLegacy {...props} />;
}

function TradingPanelLegacy({ marketData, isActive, source = "polymarket", kalshiTicker, dflowMints, predictMarketId, conditionId, eventSlug, eventTitle, onOutcomeChange, initialOutcomeIndex, initialMode, contextHeader }: TradingPanelProps) {
  const t = useT();
  const searchParams = useSearchParams();
  const router = useRouter();
  const queryClient = useQueryClient();

  // Detect market expiry in real-time (for crypto series with known endDate)
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!marketData.endDate || !isActive) return;
    const endMs = new Date(marketData.endDate).getTime();
    const check = () => {
      if (Date.now() >= endMs) setExpired(true);
    };
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [marketData.endDate, isActive]);

  const marketEnded = !isActive || expired;

  const [tradingModal, setTradingModal] = useState<{
    isOpen: boolean;
    outcome: "YES" | "NO";
    actualOutcome?: string;
    outcomeIndex?: number;
  }>({ isOpen: false, outcome: "YES" });

  // UserMe context for ninja mode & quick buy amounts
  const { user: userMe, loading: userMeLoading } = useUserMe();

  // Quick Trade state - optimistic default to Quick Trade (matches `?? true` fallback).
  // Skipping the skeleton branch means the panel renders the final layout immediately;
  // only users who disabled ninja mode see a one-time shift when `userMe` syncs.
  const [ninjaModeLocal, setNinjaModeLocal] = useState<boolean | null>(null);
  // On desktop (≥lg) the TradingPanel is always rendered inline in a
  // persistent sidebar, so the "not Quick Trade" branch (which opens the
  // full-screen TradingModal) is never the right UX — the inline panel IS
  // the trade surface there. Force Quick Trade ON for ≥lg viewports; keep
  // the user's toggle on mobile where the modal is still useful.
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  const userNinjaMode = ninjaModeLocal ?? (userMe?.ninja_mode_enabled ?? true);
  const ninjaMode = isDesktop ? true : userNinjaMode;
  const ninjaModeLoaded = true;
  const [selectedOutcomeIndex, setSelectedOutcomeIndexRaw] = useState<0 | 1>(
    initialOutcomeIndex === 0 || initialOutcomeIndex === 1 ? initialOutcomeIndex : 0
  );
  const setSelectedOutcomeIndex = (idx: 0 | 1) => {
    setSelectedOutcomeIndexRaw(idx);
    onOutcomeChange?.(idx);
    // Mirror the selection into ?outcome=yes|no so reload/share lands on the
    // same side. replaceState keeps the back button pointed at the previous
    // page instead of toggling between Yes/No in history.
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("outcome", idx === 0 ? "yes" : "no");
      window.history.replaceState({}, "", url.toString());
    }
  };
  // Respond to prop changes (e.g., when selectedMarket changes in EventSidebar)
  useEffect(() => {
    if (initialOutcomeIndex === 0 || initialOutcomeIndex === 1) {
      setSelectedOutcomeIndexRaw(initialOutcomeIndex);
    }
  }, [initialOutcomeIndex]);
  const [amount, setAmount] = useState<number>(10);
  const [amountInput, setAmountInput] = useState<string>("10"); // String for input display
  const quickBuyAmounts: number[] = userMe?.quick_buy_amounts && Array.isArray(userMe.quick_buy_amounts) ? userMe.quick_buy_amounts : [];
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Quick trade mode (buy/sell)
  const [quickTradeMode, setQuickTradeMode] = useState<"buy" | "sell">(initialMode ?? "buy");
  // Limit order state
  const [orderType, setOrderType] = useState<"market" | "limit" | "split-merge">("market");
  const [limitPrice, setLimitPrice] = useState<string>("");
  // Polymarket-style expiration presets. EOD + Custom resolve to an absolute
  // datetime, so `expiresIn` (seconds from now) is computed at submit time via
  // `computeExpiresIn()` — keeping a mode string here lets the UI stay reactive
  // without re-resolving to a stale epoch.
  const [expirationMode, setExpirationMode] = useState<
    "none" | "5m" | "1h" | "12h" | "24h" | "eod" | "custom"
  >("none");
  const [customExpirationDate, setCustomExpirationDate] = useState<string>("");
  // Amount input mode (buy-limit only). Shares mode mirrors Polymarket's
  // ticket — user types a share count and the USD cost + potential return
  // recalc off `limitPrice`. Market orders + sell mode stay in USD/shares
  // semantics respectively (unchanged).
  const [amountMode, setAmountMode] = useState<"usd" | "shares">("usd");
  // Post-only = maker-only. Forces the CLOB to reject the order if it would
  // immediately cross the spread. Required to qualify for Polymarket liquidity
  // rewards + maker rebates (taker fills don't score).
  const [postOnly, setPostOnly] = useState<boolean>(false);

  // Polymarket rewards params for this market (for post-only "earn rewards"
  // guidance). `maxSpread` = max distance from midpoint (in cents) for an
  // order to score; `minSize` = minimum order size in shares; `ratePerDay` =
  // USD pool distributed per day across all eligible makers.
  const rewardsParamsQ = useMarketRewardsParams(
    conditionId,
    source === "polymarket" && orderType === "limit",
  );
  const rewardsParams = rewardsParamsQ.data;
  const marketIsRewarded = !!rewardsParams && rewardsParams.ratePerDay > 0;
  // Split/Merge state
  const [splitMergeAmount, setSplitMergeAmount] = useState<string>("");
  const [splitMergeLoading, setSplitMergeLoading] = useState(false);
  const [splitMergeSuccess, setSplitMergeSuccess] = useState<string | null>(null);
  const [splitMergeError, setSplitMergeError] = useState<string | null>(null);
  // Raw position rows used by sell mode + split/merge. Matches by Polymarket
  // CLOB token id. Endpoint is /api/portfolio/positions which is PM-first
  // (camelCase `tokenId`/`shares`) so the field names follow that shape.
  const [userPositions, setUserPositions] = useState<Array<{ outcome?: string; shares?: number; size?: number; tokenId?: string; token_id?: string; conditionId?: string }>>([]);
  // Sell mode custom % input (one-off, not persisted — see git log for option 2)
  const [customSellPct, setCustomSellPct] = useState<string>("");
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelHovered] = useState(false);
  const positionsRetryTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  const inlineButtonsRef = useRef<HTMLDivElement>(null);
  // Ref on the main execute button so the trade-success confetti can be
  // launched from the button's position (upward) instead of the screen
  // center — matches the Place Trade sidebar UX (no modal, so no central
  // explosion).
  const executeButtonRef = useRef<HTMLButtonElement>(null);

  // Auth & Trading hooks
  const { isLoggedIn, login, currentWalletAddress } = useAuth();
  const { isEnabled: tradingEnabled, walletAddress: pmSafeWallet, enableTrading, isLoading: enablingTrading, isCheckingBackend } = useEnableTrading({ forceBackendCheck: source === 'polymarket' });
  const { balance: usdcBalance } = usePolymarketBalanceContext();
  const { openKalshi, openPredict } = useTradingModalStore();
  // Source-aware deposit entry points. When the "Add $X & Buy OUTCOME"
  // execute-button state is triggered we route to the right surface
  // (Polymarket card/wallet modal, Predict BSC USDT modal, or Privy's
  // Solana fundWallet for Kalshi).
  const { open: openPolymarketDeposit } = useDepositModalStore();
  const { open: openBscDeposit } = useBscDepositModalStore();
  const { fundWallet: fundSolanaWallet } = useFundSolanaWallet();

  // Solana wallet + balance for Kalshi
  const { balance: solanaBalance, solanaAddress } = useSolanaUSDCBalance();

  // Predict balance
  const [predictBalance, setPredictBalance] = useState<number | null>(null);
  useEffect(() => {
    if (source !== 'predict' || !isLoggedIn) return;
    let mounted = true;
    const fetchBalance = async () => {
      try {
        const res = await authFetch("/api/trading/predict/balance");
        if (res.ok && mounted) {
          const data = await res.json();
          setPredictBalance(data.balance?.usdt ?? data.usdt ?? 0);
        }
      } catch {}
    };
    fetchBalance();
    const interval = setInterval(fetchBalance, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, [source, isLoggedIn]);

  // Wallet copy state
  const [walletCopied, setWalletCopied] = useState(false);

  // Unified wallet + balance across all sources
  const displayWallet = source === 'polymarket' ? pmSafeWallet
    : source === 'kalshi' ? solanaAddress
    : currentWalletAddress; // predict = EOA on BNB
  const displayBalance = source === 'polymarket' ? usdcBalance
    : source === 'kalshi' ? solanaBalance
    : predictBalance;
  const hasDisplayBalance = Number.isFinite(displayBalance);
  const safeDisplayBalance = hasDisplayBalance ? Number(displayBalance) : null;
  const balanceSymbol = source === 'predict' ? 'USDT' : source === 'kalshi' ? 'USDC' : 'USDC.e';
  // `insufficientBalance` compares against notional USD — declared further below
  // after shares/notional are computed (shares-mode buy-limit needs the price
  // conversion to know the true USD cost).

  const copyWallet = () => {
    if (displayWallet) {
      navigator.clipboard.writeText(displayWallet);
      setWalletCopied(true);
      setTimeout(() => setWalletCopied(false), 2000);
    }
  };

  const refreshUserPositions = useCallback(() => {
    const needsPositions = orderType === 'split-merge' || quickTradeMode === 'sell';
    if (!needsPositions || !isLoggedIn || !conditionId) return Promise.resolve();

    return authFetch(`/api/portfolio/positions?status=open`)
      .then(r => r.json())
      .then(data => {
        if (data.positions) setUserPositions(data.positions);
      })
      .catch(() => {});
  }, [orderType, quickTradeMode, isLoggedIn, conditionId]);

  // Fetch positions when split-merge tab is active OR when user switches to
  // sell mode — so the panel knows how many shares are owned per outcome and
  // can show correct presets/max/validation.
  useEffect(() => {
    refreshUserPositions();
  }, [refreshUserPositions]);

  useEffect(() => {
    const handlePositionsRefresh = () => {
      refreshUserPositions();
      [1500, 4000].forEach((delay) => {
        const timeoutId = setTimeout(() => {
          positionsRetryTimeoutsRef.current.delete(timeoutId);
          refreshUserPositions();
        }, delay);
        positionsRetryTimeoutsRef.current.add(timeoutId);
      });
    };

    window.addEventListener("positions:refresh", handlePositionsRefresh);
    window.addEventListener("portfolio:refresh", handlePositionsRefresh);

    return () => {
      positionsRetryTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      positionsRetryTimeoutsRef.current.clear();
      window.removeEventListener("positions:refresh", handlePositionsRefresh);
      window.removeEventListener("portfolio:refresh", handlePositionsRefresh);
    };
  }, [refreshUserPositions]);

  // Execute split or merge
  const executeSplitMerge = async (action: "split" | "merge") => {
    if (!isLoggedIn) { login(); return; }
    if (!conditionId) return;
    const amt = parseFloat(splitMergeAmount);
    if (!amt || amt <= 0) return;
    setSplitMergeLoading(true);
    setSplitMergeError(null);
    setSplitMergeSuccess(null);
    try {
      const res = await authFetch(`/api/trade/${action}`, {
        method: "POST",
        body: JSON.stringify({ conditionId, amount: amt }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || `${action} failed`);
      setSplitMergeSuccess(action === 'split' ? 'Split successful!' : 'Merge successful!');
      setSplitMergeAmount("");
      celebrateSimple();
      setTimeout(() => setSplitMergeSuccess(null), 3000);
      // Refresh positions
      refreshUserPositions();
    } catch (err: any) {
      setSplitMergeError(err.message || `${action} failed`);
      setTimeout(() => setSplitMergeError(null), 5000);
    } finally {
      setSplitMergeLoading(false);
    }
  };

  // Lookup helpers — tolerate both response shapes so the trading panel
  // works whether positions come from /api/portfolio/positions (camelCase
  // tokenId/shares) or /api/users/positions (snake_case token_id/size).
  const findPositionByTokenId = (tid: string) => userPositions.find(p =>
    (p.tokenId && p.tokenId === tid) || (p.token_id && p.token_id === tid)
  );
  const getPositionSize = (p: { shares?: number; size?: number } | undefined) =>
    Number(p?.shares ?? p?.size ?? 0);

  // Check merge eligibility: user needs both YES and NO positions
  const hasBothOutcomes = conditionId && userPositions.length >= 2 &&
    marketData.clobTokenIds.slice(0, 2).every(tid =>
      getPositionSize(findPositionByTokenId(tid)) > 0
    );
  const maxMergeAmount = hasBothOutcomes
    ? Math.min(...marketData.clobTokenIds.slice(0, 2).map(tid =>
        getPositionSize(findPositionByTokenId(tid))
      ))
    : 0;

  // Real-time prices — source-aware
  const priceContext = useMarketPriceOptional();

  // Kalshi: DFlow real-time prices via WebSocket
  const { getPrice: getDFlowPrice } = useDFlowRealtimePrice({
    tickers: source === 'kalshi' && kalshiTicker ? [kalshiTicker] : [],
    enabled: source === 'kalshi',
  });
  const kalshiLive = source === 'kalshi' && kalshiTicker ? getDFlowPrice(kalshiTicker) : null;

  // Predict: real-time prices via WebSocket
  const { getPrice: getPredictPrice } = usePredictRealtimePrice({
    marketIds: source === 'predict' && predictMarketId ? [predictMarketId] : [],
    enabled: source === 'predict',
  });
  const predictLive = source === 'predict' && predictMarketId ? getPredictPrice(predictMarketId) : null;
  const { data: dflowPositions = [] } = useDFlowPositionsQuery(source === "kalshi" && quickTradeMode === "sell");
  const { data: kalshiExchangeStatus } = useKalshiExchangeStatus(source === "kalshi");
  const kalshiTradingUnavailable = source === "kalshi" && !!kalshiExchangeStatus && (!kalshiExchangeStatus.exchangeActive || !kalshiExchangeStatus.tradingActive);
  const kalshiTradeDisabledReason = !kalshiExchangeStatus?.exchangeActive
    ? "Kalshi exchange is currently closed."
    : !kalshiExchangeStatus?.tradingActive
      ? "Kalshi trading is currently paused."
      : null;

  // Unified price resolution: real-time hook > MarketPriceProvider > static fallback
  const buyPrices = (() => {
    // Polymarket: use MarketPriceProvider context
    if (priceContext?.buyPrices) return priceContext.buyPrices;
    // Kalshi: use DFlow real-time
    if (kalshiLive) {
      const yFb = marketData.outcomePrices[0] ?? 0.5;
      const nFb = marketData.outcomePrices[1] ?? 0.5;
      return {
        buyYes: kalshiLive.yesAsk ?? kalshiLive.yesBid ?? yFb,
        buyNo: kalshiLive.noAsk ?? kalshiLive.noBid ?? nFb,
        isRealOrderbook: kalshiLive.yesBid !== null || kalshiLive.yesAsk !== null,
      };
    }
    // Predict: use Predict real-time
    if (predictLive) {
      const mid = predictLive.midPrice ?? marketData.outcomePrices[0] ?? 0.5;
      return {
        buyYes: predictLive.bestAsk ?? mid,
        buyNo: 1 - (predictLive.bestBid ?? mid),
        isRealOrderbook: predictLive.bestBid !== null || predictLive.bestAsk !== null,
      };
    }
    // Fallback: static prices
    return { buyYes: marketData.outcomePrices[0] ?? 0.5, buyNo: marketData.outcomePrices[1] ?? 0.5, isRealOrderbook: false };
  })();

  // Styled error toast (since Toaster is configured as unstyled)
  const showError = (message: string) => {
    toast.custom(
      (toastId) => (
        <div className="flex items-start gap-3 p-4 bg-[#1a1a1a] border border-[#FF1744]/30 rounded-[5px] shadow-lg max-w-sm">
          <FiAlertCircle className="w-5 h-5 text-[#FF1744] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-inter-tight text-sm font-medium text-white">{t("tradingPanel.tradeError")}</p>
            <p className="font-satoshi text-xs text-gray-400 mt-1">{message}</p>
          </div>
          <button
            onClick={() => toast.dismiss(toastId)}
            className="text-gray-500 hover:text-white transition-colors"
          >
            &times;
          </button>
        </div>
      ),
      { duration: 5000, position: "bottom-right" }
    );
  };

  // Save ninja mode setting when toggled
  const toggleNinjaMode = async () => {
    const newValue = !(ninjaMode ?? true);
    setNinjaModeLocal(newValue);

    // Persist locally so the next page load can bootstrap the correct layout
    try {
      localStorage.setItem("frenflow-ninja-mode", String(newValue));
    } catch {
      // Safari private mode or storage disabled — ignore
    }

    // Save to backend if logged in
    if (isLoggedIn) {
      try {
        await authFetch("/api/users/settings", {
          method: "POST",
          body: JSON.stringify({ ninja_mode_enabled: newValue }),
        });
      } catch (err) {
        // Silently fail - UI already updated
      }
    }
  };

  // On mount, sync local state from localStorage so returning users with a
  // disabled ninja mode don't flash the Quick Trade layout before userMe loads.
  useEffect(() => {
    try {
      const stored = localStorage.getItem("frenflow-ninja-mode");
      if (stored === "true" && ninjaModeLocal === null) setNinjaModeLocal(true);
      else if (stored === "false" && ninjaModeLocal === null) setNinjaModeLocal(false);
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open trading modal or select outcome from URL parameters.
  // Accepts:
  //   ?trade=yes              — legacy format
  //   ?action=trade&outcome=… — explicit action format
  //   ?outcome=…              — bare format used by event-page deep links from /portfolio, bot, etc.
  //
  // Runs ONCE per mount (ignore searchParams changes). User clicks own the state
  // after mount — re-reading would fight back because useSearchParams lags behind
  // window.history.replaceState, causing a yes↔no flip loop.
  const urlSyncedRef = useRef(false);
  useEffect(() => {
    if (urlSyncedRef.current) return;
    // When the parent passes an explicit initialOutcomeIndex (e.g., the sell
    // modal opened from /portfolio for a NO position) it owns the side. The
    // page URL still carries `?outcome=…` from prior in-page selections —
    // reading it here would flip the modal to the wrong side. Mark the URL
    // sync as done so subsequent param changes can't override either.
    if (initialOutcomeIndex === 0 || initialOutcomeIndex === 1) {
      urlSyncedRef.current = true;
      return;
    }
    const tradeParam = searchParams.get('trade');
    const outcomeParam = searchParams.get('outcome');

    if (!isActive) return;
    if (!ninjaModeLoaded) return;
    urlSyncedRef.current = true;

    // Determine outcome from any supported format. `action=trade` is accepted but no longer required
    // — the presence of `outcome` (or `trade`) is enough to trigger the pre-selection flow.
    let targetOutcome: string | null = null;
    if (tradeParam) {
      targetOutcome = tradeParam;
    } else if (outcomeParam) {
      targetOutcome = outcomeParam;
    }

    if (targetOutcome) {
      const normalizedOutcome = targetOutcome.toLowerCase();

      // Find matching outcome index in market data
      const matchingIndex = marketData.outcomes.findIndex(
        o => o.toLowerCase() === normalizedOutcome
      );

      if (ninjaMode && ninjaModeLoaded) {
        // In Quick Trade: select the outcome
        if (matchingIndex !== -1) {
          setSelectedOutcomeIndex(matchingIndex as 0 | 1);
        } else {
          // Fallback for yes/no when outcomes are different (e.g., Up/Down)
          setSelectedOutcomeIndex(normalizedOutcome === 'yes' || normalizedOutcome === 'up' ? 0 : 1);
        }
      } else if (!ninjaMode && ninjaModeLoaded) {
        // Not in Quick Trade: open trading modal. Resolve via matchingIndex
        // (the index of the URL outcome inside marketData.outcomes) so the
        // mapping works for any market type (Yes/No, Up/Down, Over/Under,
        // sports). Only fall back to the binary string match when the URL
        // outcome doesn't appear in the market's outcomes array.
        const idx = matchingIndex !== -1
          ? matchingIndex
          : (normalizedOutcome === 'yes' || normalizedOutcome === 'up' ? 0 : 1);
        const actualOutcome = marketData.outcomes[idx] || undefined;
        setTradingModal({
          isOpen: true,
          outcome: idx === 0 ? 'YES' : 'NO',
          actualOutcome,
          outcomeIndex: idx,
        });
      }

      // Clean URL: drop the legacy deep-link triggers after we've processed
      // them. `outcome` used to be stripped here too, but it's now persisted
      // by setSelectedOutcomeIndex so Yes/No selections survive reloads and
      // are shareable — removing it here erased the state milliseconds after
      // the user clicked Place Trade.
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.delete('trade');
        url.searchParams.delete('action');
        window.history.replaceState({}, '', url.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, ninjaModeLoaded]);

  // Reset success state
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Check if market is binary (exactly 2 outcomes) - Quick Trade only works for binary markets
  const isBinaryMarket = marketData.outcomes.length === 2;

  // Price calculations - use buyPrices for buttons, livePrices for % CHANCE
  const outcome1Price = buyPrices.buyYes; // Price to buy YES
  const outcome2Price = buyPrices.buyNo;  // Price to buy NO
  const hideSmartMoneyForEphemeralCrypto =
    source === 'polymarket'
    && (
      marketData.slug?.match(/-(?:5m|15m|1h|4h)-/)
      || marketData.question?.toLowerCase().includes('up or down')
      // Daily crypto price-level markets ("Will Bitcoin reach $76K on
      // April 20?") are also too short-lived to accumulate smart-money
      // activity — the endpoint returns zeros, so the badge shows a
      // skeleton and then disappears. Match the reach/dip phrasing.
      || /\b(?:bitcoin|btc|ethereum|eth|solana|sol|xrp|doge|dogecoin|hyperliquid|hype|bnb)\b.*\b(?:reach|dip\s+to)\s+\$/i.test(marketData.question || '')
    );

  // Flash effect on price change
  const [flash1, setFlash1] = useState<'up' | 'down' | null>(null);
  const [flash2, setFlash2] = useState<'up' | 'down' | null>(null);
  const prevPricesRef = useRef({ p1: outcome1Price, p2: outcome2Price });

  useEffect(() => {
    const prev = prevPricesRef.current;
    if (Math.abs(outcome1Price - prev.p1) > 0.001) {
      setFlash1(outcome1Price > prev.p1 ? 'up' : 'down');
      setTimeout(() => setFlash1(null), 600);
    }
    if (Math.abs(outcome2Price - prev.p2) > 0.001) {
      setFlash2(outcome2Price > prev.p2 ? 'up' : 'down');
      setTimeout(() => setFlash2(null), 600);
    }
    prevPricesRef.current = { p1: outcome1Price, p2: outcome2Price };
  }, [outcome1Price, outcome2Price]);
  // Display labels for the BUY/SELL buttons. `marketData.outcomes` is the
  // ordered pair from the API (Polymarket data-api / Kalshi / Predict) —
  // never re-derive labels from the binary slot. The trailing "YES"/"NO"
  // fallbacks survive only as a last-ditch guard if a caller forgot to
  // populate `outcomes`; if they ever fire visibly that's a bug in the
  // call site, not in this component.
  const rawOutcome1 = marketData.outcomes[0];
  const rawOutcome2 = marketData.outcomes[1];
  const outcome1Label = (eventSlug && rawOutcome1
    ? getOutcomeAbbr(rawOutcome1, eventSlug, eventTitle)
    : rawOutcome1)?.toUpperCase() || "YES";
  const outcome2Label = (eventSlug && rawOutcome2
    ? getOutcomeAbbr(rawOutcome2, eventSlug, eventTitle)
    : rawOutcome2)?.toUpperCase() || "NO";
  // Raw outcome strings for downstream logic (matching against API rows,
  // building telemetry events). Empty string when missing — let the API
  // layer or null-check guard handle it instead of forcing "Yes"/"No".
  const outcome1Value = rawOutcome1 || "";
  const outcome2Value = rawOutcome2 || "";
  // Localized display labels: the canonical binary "Yes"/"No" become the
  // user's locale (Sí/No). Team / Over-Under / multi-outcome labels pass
  // through untouched. The English `*Label` values above stay the source of
  // truth for trade payloads, telemetry and Dextools events — only the
  // strings the user reads are localized.
  const localizeYesNo = (raw: string | undefined, fallback: string): string => {
    const r = (raw || "").trim().toLowerCase();
    if (r === "yes") return t("smartMoney.yes");
    if (r === "no") return t("smartMoney.no");
    return fallback;
  };
  const outcome1LabelDisplay = localizeYesNo(rawOutcome1, outcome1Label);
  const outcome2LabelDisplay = localizeYesNo(rawOutcome2, outcome2Label);
  const selectedOutcomeLabelDisplay = selectedOutcomeIndex === 0 ? outcome1LabelDisplay : outcome2LabelDisplay;

  // `amount` is DOLLARS in buy mode but SHARES in sell mode — both modes
  // share the same state so switching doesn't lose the user's input.
  // Defined early so the price below can branch on buy vs sell.
  const isSellMode = quickTradeMode === "sell";

  // Current selection price.
  //   BUY  executes against the best ASK (what you PAY).
  //   SELL executes against the best BID (what you RECEIVE).
  // `outcome1Price`/`outcome2Price` are the real, sanity-checked ASKS from
  // getBuyPrices (= bestAsk of each outcome token). In Polymarket's binary
  // CLOB the two outcome books are exactly complementary, so:
  //   bestBid(selected) = 1 − bestAsk(other outcome)
  // Verified empirically against clob.polymarket.com/book (diff 0.0000) and
  // against the py-clob-client semantics. This yields the REAL price the sell
  // fills at — never the ask.
  // Bug fix (2026-06-09): sell mode previously used the ASK, so in an illiquid
  // wide-spread market the modal showed e.g. 94¢ while the user actually
  // received the 49¢ bid (user am8519672). Now it shows the executable bid.
  const askPrice = selectedOutcomeIndex === 0 ? outcome1Price : outcome2Price;
  const bidPrice = Math.min(1, Math.max(0, 1 - (selectedOutcomeIndex === 0 ? outcome2Price : outcome1Price)));
  const currentPrice = isSellMode ? bidPrice : askPrice;
  const selectedOutcomeValue = selectedOutcomeIndex === 0 ? outcome1Value : outcome2Value;
  const selectedOutcomeLabel = selectedOutcomeIndex === 0 ? outcome1Label : outcome2Label;
  const effectivePrice = orderType === "limit" && limitPrice ? parseFloat(limitPrice) / 100 : currentPrice;
  // Shares the user owns for the currently selected outcome (resolved via the
  // shared lookup helpers defined above so split/merge and sell mode agree).
  const ownedShares = (() => {
    if (source === "kalshi" && kalshiTicker) {
      const selectedOutcome = selectedOutcomeIndex === 0 ? "yes" : "no";
      return dflowPositions
        .filter((position) => position.kalshiTicker === kalshiTicker && position.outcome?.toLowerCase() === selectedOutcome)
        .reduce((sum, position) => sum + (Number(position.size) || 0), 0);
    }
    if (!marketData.clobTokenIds || marketData.clobTokenIds.length < 2) return 0;
    const tid = marketData.clobTokenIds[selectedOutcomeIndex];
    if (!tid) return 0;
    return getPositionSize(findPositionByTokenId(tid));
  })();
  // Shares to trade + notional dollar value for the current mode.
  //   sell            → `amount` is SHARES, notional = amount * price
  //   buy USD mode    → `amount` is USD, shares = amount / price
  //   buy shares mode → `amount` is SHARES, notional = amount * price (Polymarket-style)
  // Shares input mode is scoped to buy-LIMIT — market orders always USD.
  const isSharesInput = !isSellMode && orderType === "limit" && amountMode === "shares";
  const shares = isSellMode
    ? amount
    : isSharesInput
      ? amount
      : (amount > 0 && effectivePrice > 0 ? amount / effectivePrice : 0);
  const notionalUsd = isSellMode
    ? shares * effectivePrice
    : isSharesInput
      ? amount * effectivePrice
      : amount;
  const potentialProfit = isSellMode ? notionalUsd : (shares - notionalUsd);
  // Insufficient balance compares the cash needed (notional) not the raw
  // `amount` input — critical in shares mode where `amount` is share count.
  const insufficientBalance = quickTradeMode === "buy"
    && safeDisplayBalance !== null
    && safeDisplayBalance < notionalUsd;

  // Resolve `expiresIn` (seconds from now) at submit time. EOD + Custom depend
  // on wall-clock so computing eagerly would drift.
  const computeExpiresIn = (): number | undefined => {
    const now = Date.now();
    switch (expirationMode) {
      case "none": return undefined;
      case "5m":   return 300;
      case "1h":   return 3600;
      case "12h":  return 43200;
      case "24h":  return 86400;
      case "eod": {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return Math.max(60, Math.floor((d.getTime() - now) / 1000));
      }
      case "custom": {
        if (!customExpirationDate) return undefined;
        const ts = new Date(customExpirationDate).getTime();
        if (!ts || ts <= now) return undefined;
        return Math.max(60, Math.floor((ts - now) / 1000));
      }
    }
  };

  // Listen for "open trade" events from other components (e.g., MyPositions empty state)
  useEffect(() => {
    const handleOpenTrade = () => {
      // Open modal for the leading outcome (higher price = winning)
      const leadingIsYes = outcome1Price >= outcome2Price;
      setTradingModal({
        isOpen: true,
        outcome: leadingIsYes ? "YES" : "NO",
        actualOutcome: leadingIsYes ? outcome1Value : outcome2Value,
        outcomeIndex: leadingIsYes ? 0 : 1,
      });
    };
    window.addEventListener("frenflow:open-trade", handleOpenTrade);
    return () => window.removeEventListener("frenflow:open-trade", handleOpenTrade);
  }, [outcome1Price, outcome2Price, outcome1Value, outcome2Value]);

  // Execute trade
  const executeTrade = async () => {
    if (!isLoggedIn) { login(); return; }

    if (source === 'kalshi') {
      if (kalshiTradingUnavailable) {
        showError(kalshiTradeDisabledReason || "Kalshi trading is currently unavailable.");
        return;
      }
      if (quickTradeMode === "buy" && safeDisplayBalance !== null && safeDisplayBalance < amount) {
        showError(`Insufficient balance. You need $${amount.toFixed(2)} but have $${safeDisplayBalance.toFixed(2)} ${balanceSymbol}`);
        return;
      }
      if (quickTradeMode === "sell" && amount > ownedShares) {
        showError(`Sell amount exceeds your position. You own ${ownedShares.toFixed(2)} shares.`);
        return;
      }
      setIsLoading(true);
      try {
        const res = await authFetch("/api/dflow/execute", {
          method: "POST",
          body: JSON.stringify({
            kalshiTicker: kalshiTicker || marketData.slug,
            marketTitle: marketData.question,
            action: quickTradeMode,
            outcome: selectedOutcomeIndex === 0 ? "yes" : "no",
            amountUsd: quickTradeMode === "buy" ? amount : undefined,
            shares: quickTradeMode === "sell" ? amount : undefined,
            yesMint: dflowMints?.yesMint,
            noMint: dflowMints?.noMint,
          }),
        });
        const result = await res.json();
        if (!res.ok || !result.success) throw new Error(result.error || "Trade failed");
        notifyPortfolioRefresh();
        window.dispatchEvent(new Event("dflow:refresh"));
        queryClient.invalidateQueries({ queryKey: ["dflow", "positions"] });
        setShowSuccess(true);
        celebrateFromElement(executeButtonRef.current);
        toast.custom((t) => (
          <TradeToast onClose={() => toast.dismiss(t)} data={{
            side: quickTradeMode.toUpperCase() as "BUY" | "SELL",
            outcome: selectedOutcomeLabel,
            // Prefer the backend's actual execution data over the FE's
            // pre-trade calc — `currentPrice`/`shares` are derived from
            // the live bestAsk at the moment the toast renders, which
            // can differ from the real fill price by several cents on
            // fast markets (crypto up/down 5m). Showing the backend's
            // executedPrice/Size keeps the toast honest and consistent
            // with the avg_price persisted to positions.
            shares: Number(result?.executedSize) || shares,
            price: Number(result?.executedPrice) || currentPrice,
            notionalValue: amount,
            marketQuestion: marketData.question, marketSlug: marketData.slug,
            marketImage: marketData.image || null, source: "kalshi",
          }} />
        ), { duration: 6000, position: "bottom-right" });
      } catch (err: any) {
        showError(err.message || "Trade failed");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (source === 'predict') {
      if (quickTradeMode === "buy" && safeDisplayBalance !== null && safeDisplayBalance < amount) {
        showError(`Insufficient balance. You need $${amount.toFixed(2)} but have $${safeDisplayBalance.toFixed(2)} ${balanceSymbol}`);
        return;
      }
      setIsLoading(true);
      try {
        const res = await authFetch("/api/trading/predict/execute", {
          method: "POST",
          body: JSON.stringify({
            marketId: predictMarketId || marketData.slug,
            outcome: selectedOutcomeValue,
            outcomeIndex: selectedOutcomeIndex,
            amount,
            price: currentPrice,
            side: quickTradeMode.toUpperCase(),
          }),
        });
        const result = await res.json();
        if (!res.ok || !result.success) throw new Error(result.error || "Trade failed");
        setShowSuccess(true);
        celebrateFromElement(executeButtonRef.current);
        toast.custom((t) => (
          <TradeToast onClose={() => toast.dismiss(t)} data={{
            side: quickTradeMode.toUpperCase() as "BUY" | "SELL",
            outcome: selectedOutcomeLabel,
            shares: Number(result?.executedSize) || shares,
            price: Number(result?.executedPrice) || currentPrice,
            notionalValue: amount,
            marketQuestion: marketData.question, marketSlug: marketData.slug,
            marketImage: marketData.image || null, source: "predict",
          }} />
        ), { duration: 6000, position: "bottom-right" });
      } catch (err: any) {
        showError(err.message || "Trade failed");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Polymarket flow
    if (!tradingEnabled) {
      enableTrading();
      return;
    }

    // No minimum validation - let the backend/Polymarket handle any limits

    // Balance check only applies to BUY (needs USDC). SELL needs shares, not USDC.
    // Use notionalUsd not `amount` — in shares-mode buy-limit `amount` is a
    // share count, the USD cost is `amount * price`.
    if (quickTradeMode === "buy" && safeDisplayBalance !== null && safeDisplayBalance < notionalUsd) {
      showError(`Insufficient balance. You need $${notionalUsd.toFixed(2)} but have $${safeDisplayBalance.toFixed(2)} ${balanceSymbol}`);
      return;
    }

    setIsLoading(true);

    try {
      let response: Response;

      const tradeSide = quickTradeMode.toUpperCase();
      const selectedTokenId = marketData.clobTokenIds?.[selectedOutcomeIndex];
      if (quickTradeMode === "sell") {
        // Sell order — `amount` is already in SHARES (user typed shares, not USD).
        // For limit sells we pass limitPrice + postOnly; backend routes limit
        // orders through placeLimitOrder. Market sells ignore price/postOnly.
        const isLimitSell = orderType === "limit" && !!limitPrice;
        const sellExpiresIn = isLimitSell ? computeExpiresIn() : undefined;
        response = await authFetch("/api/trade/sell", {
          method: "POST",
          body: JSON.stringify({
            marketSlug: marketData.slug,
            outcome: selectedOutcomeValue,
            shares: amount,
            price: isLimitSell ? parseFloat(limitPrice) / 100 : currentPrice,
            orderType,
            ...(isLimitSell && sellExpiresIn ? { expiresIn: sellExpiresIn } : {}),
            ...(isLimitSell && postOnly ? { postOnly: true } : {}),
            ...(selectedTokenId ? { tokenId: selectedTokenId } : {}),
          }),
        });
      } else if (orderType === "limit" && limitPrice) {
        // Limit buy order — pass tokenId to avoid Gamma slug lookup.
        // Backend expects `amount` in USD for BUY, so in shares mode we
        // convert via the typed limit price (what the user actually pays).
        const buyExpiresIn = computeExpiresIn();
        response = await authFetch("/api/trade/limit-order", {
          method: "POST",
          body: JSON.stringify({
            marketSlug: marketData.slug,
            outcome: selectedOutcomeValue,
            side: tradeSide,
            price: parseFloat(limitPrice) / 100,
            amount: amountMode === "shares" ? notionalUsd : amount,
            expiresIn: buyExpiresIn || undefined,
            postOnly: postOnly || undefined,
            ...(selectedTokenId ? { tokenId: selectedTokenId } : {}),
          }),
        });
      } else {
        // Market buy order — pass tokenId to avoid Gamma slug lookup (fixes multi-outcome)
        response = await authFetch("/api/trade/execute", {
          method: "POST",
          body: JSON.stringify({
            marketSlug: marketData.slug,
            outcome: selectedOutcomeValue,
            amount,
            price: currentPrice,
            side: tradeSide,
            ...(selectedTokenId ? { tokenId: selectedTokenId } : {}),
          }),
        });
      }

      const result = await response.json();

      if (!response.ok) {
        // Build detailed error message
        let errorMessage = result.error || "Failed to place order";
        if (result.details) {
          const fieldErrors = Object.entries(result.details)
            .map(([field, errors]) => `${field}: ${(errors as string[]).join(", ")}`)
            .join("; ");
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
        throw new Error(errorMessage);
      }

      setShowSuccess(true);
      celebrateFromElement(executeButtonRef.current);
      reportDextoolsConversion(marketData.slug, `trade_${selectedOutcomeLabel.toLowerCase()}`, amount);
      notifyPortfolioRefresh();

      // Invalidate open orders cache so badges update immediately
      const isLimitOrder = orderType === "limit" && !!limitPrice;
      if (isLimitOrder) {
        queryClient.invalidateQueries({ queryKey: ["user-open-orders-event"] });
      }
      toast.custom(
        (t) => (
          <TradeToast
            onClose={() => toast.dismiss(t)}
            data={{
              side: quickTradeMode.toUpperCase() as "BUY" | "SELL",
              outcome: selectedOutcomeLabel,
              // Limit orders haven't filled yet, so the user-typed
              // `limitPrice` is the honest number to show; the fill
              // (if any) will surface later via the orders panel.
              // Market orders fall back to the backend's executedPrice
              // — the FE `currentPrice` is the live bestAsk (buy) / bestBid
              // (sell) and can have drifted between click and toast render.
              shares: isLimitOrder ? shares : (Number(result?.executedSize) || shares),
              price: isLimitOrder
                ? parseFloat(limitPrice) / 100
                : (Number(result?.executedPrice) || currentPrice),
              notionalValue: notionalUsd,
              marketQuestion: marketData.question,
              marketSlug: marketData.slug,
              marketImage: marketData.image || null,
              source: source || "polymarket",
              isLimit: isLimitOrder,
            }}
          />
        ),
        { duration: 6000, position: "bottom-right" }
      );
    } catch (err: any) {
      showError(err.message || "Trade failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle button click based on mode
  const handleOutcomeClick = (outcomeIndex: number) => {
    if (ninjaMode && isBinaryMarket) {
      setSelectedOutcomeIndex(outcomeIndex as 0 | 1);
    } else if (source === 'kalshi') {
      if (!isLoggedIn) { login(); return; }
      if (kalshiTradingUnavailable) {
        showError(kalshiTradeDisabledReason || "Kalshi trading is currently unavailable.");
        return;
      }
      // Kalshi store still uses lowercase "yes"|"no" as the slot key, but
      // we propagate `outcomes` so the modal renders the real labels.
      openKalshi({
        marketTicker: kalshiTicker || marketData.slug,
        marketTitle: marketData.question,
        outcome: outcomeIndex === 0 ? 'yes' : 'no',
        currentPrice: outcomeIndex === 0 ? outcome1Price : outcome2Price,
        altPrice: outcomeIndex === 0 ? outcome2Price : outcome1Price,
        yesMint: dflowMints?.yesMint,
        noMint: dflowMints?.noMint,
        imageUrl: marketData.image || undefined,
      });
    } else if (source === 'predict') {
      if (!isLoggedIn) { login(); return; }
      const sideOutcome = marketData.outcomes[outcomeIndex] || "";
      const altOutcome = marketData.outcomes[outcomeIndex === 0 ? 1 : 0] || "";
      openPredict({
        marketId: predictMarketId || '',
        marketSlug: marketData.slug,
        outcome: outcomeIndex === 0 ? "YES" : "NO",
        outcomePrice: outcomeIndex === 0 ? outcome1Price : outcome2Price,
        marketQuestion: marketData.question,
        marketImage: marketData.image,
        altOutcomePrice: outcomeIndex === 0 ? outcome2Price : outcome1Price,
        outcomes: marketData.outcomes.length >= 2 ? marketData.outcomes.slice(0, 2) : undefined,
        actualOutcome: sideOutcome || undefined,
        altActualOutcome: altOutcome || undefined,
      });
    } else {
      // Polymarket: open trading modal. The full outcomes pair + both
      // side labels are reconstructed below at render time from
      // outcome1Value/outcome2Value, so no need to stash them in state.
      const sideOutcome = marketData.outcomes[outcomeIndex] || "";
      setTradingModal({
        isOpen: true,
        outcome: outcomeIndex === 0 ? "YES" : "NO",
        actualOutcome: sideOutcome,
        outcomeIndex: outcomeIndex,
      });
    }
  };

  // Amount presets
  const addAmount = (add: number) => {
    if (add === -1) {
      // Max button — fills with available balance (buy) or owned shares (sell).
      if (isSellMode) {
        const maxShares = Math.floor(ownedShares * 100) / 100;
        setAmount(maxShares);
        setAmountInput(String(maxShares));
      } else if (safeDisplayBalance !== null) {
        const maxAmount = Math.floor(safeDisplayBalance * 100) / 100;
        setAmount(maxAmount);
        setAmountInput(String(maxAmount));
      }
    } else {
      const newAmount = Math.max(1, amount + add);
      setAmount(newAmount);
      setAmountInput(String(newAmount));
    }
  };

  // When switching buy↔sell, reset the amount so the previous-mode value
  // doesn't leak across (e.g. "$10" becoming "10 shares").
  useEffect(() => {
    setAmount(isSellMode ? 0 : 10);
    setAmountInput(isSellMode ? "0" : "10");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSellMode]);

  return (
    <>
      <div
        ref={panelRef}
        className={`rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-4 md:p-6 transition-colors ${
          marketEnded ? "bg-gray-50 dark:bg-[#111114]" : "bg-white dark:bg-[#18181D]"
        }`}
      >
        {/* Context header — rendered inside the card so the selected market
            (image + title + alerts) sits as part of the same visual unit as
            the trade controls, instead of a floating card on top. Only
            shown on event pages via the `contextHeader` prop. */}
        {contextHeader && (
          <div className="mb-4 pb-3 border-b border-gray-200 dark:border-[#1A1A1A]">
            {contextHeader}
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {marketEnded ? (
              <GavelIcon size={20} className="text-accent-primary" />
            ) : (
              <ZapIcon size={20} className="text-accent-primary" isHovered={panelHovered} />
            )}
            <h3 className="font-monument text-lg text-gray-900 dark:text-white">
              {marketEnded ? t("tradingPanel.resolution") : t("tradingPanel.placeTrade")}
            </h3>
          </div>

          {/* Quick Trade Toggle — only rendered on mobile, where the
              non-Quick-Trade path still opens the TradingModal. On desktop
              (≥lg) the panel is always Quick Trade, so the toggle would be
              dead UI. Binary-market gate unchanged. */}
          {!marketEnded && isBinaryMarket && !isDesktop && (
            <div className="relative group">
              <button
                onClick={toggleNinjaMode}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-[5px] transition-colors ${
                  ninjaMode
                    ? "bg-gray-100 dark:bg-[#0f0f12] border border-gray-300 dark:border-gray-600"
                    : "bg-gray-100 dark:bg-[#0f0f12] border border-transparent hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                <img
                  src={cdn("/logos/ninja.png")}
                  alt="Ninja"
                  className="w-5 h-5 transition-[filter] duration-300"
                  style={{
                    filter: ninjaMode
                      ? "invert(89%) sepia(46%) saturate(1000%) hue-rotate(20deg) brightness(110%) contrast(100%)"
                      : "invert(50%) brightness(80%)"
                  }}
                />
                <span className={`text-xs font-inter-tight font-bold transition-colors ${
                  ninjaMode ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"
                }`}>
                  Quick Trade
                </span>
                {ninjaMode && (
                  <span className="w-2 h-2 rounded-[5px] bg-[#CCFF00]" />
                )}
              </button>
              {/* Tooltip */}
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1.5 bg-black/80 backdrop-blur-sm rounded-[5px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150 whitespace-nowrap z-[300] pointer-events-none">
                <span className="text-white text-[10px] font-inter-tight">{t("tradingPanel.oneClickHint")}</span>
              </div>
            </div>
          )}
        </div>

        {/* Quick Trade Active - Compact Trading Interface (only for binary markets) */}
        {ninjaMode && !marketEnded && isBinaryMarket ? (
          <div ref={inlineButtonsRef} className="space-y-4">
            {/* Buy/Sell Toggle + Order Type Dropdown */}
            <div className="flex items-center justify-between">
              <div className="flex bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] p-0.5">
                <button
                  onClick={() => setQuickTradeMode("buy")}
                  className={`px-4 py-1.5 rounded-[5px] text-xs font-inter-tight font-bold transition-colors ${
                    quickTradeMode === "buy"
                      ? "bg-white dark:bg-[#18181D] text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  Buy
                </button>
                <button
                  onClick={() => setQuickTradeMode("sell")}
                  className={`px-4 py-1.5 rounded-[5px] text-xs font-inter-tight font-bold transition-colors ${
                    quickTradeMode === "sell"
                      ? "bg-white dark:bg-[#18181D] text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  Sell
                </button>
              </div>
              {source === 'polymarket' && (
                <select
                  value={orderType}
                  onChange={(e) => setOrderType(e.target.value as "market" | "limit" | "split-merge")}
                  className="px-2 py-1.5 text-[11px] font-inter-tight font-bold border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] bg-white dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300 outline-none cursor-pointer"
                >
                  <option value="market">Market</option>
                  <option value="limit">Limit</option>
                  {conditionId && <option value="split-merge">Split/Merge</option>}
                </select>
              )}
            </div>

            {/* Smart Money nudge — Polymarket only */}
            {source === 'polymarket' && conditionId && !hideSmartMoneyForEphemeralCrypto && (
              <SmartMoneyBadge conditionId={conditionId} variant="inline" className="mb-2" />
            )}

            {/* Outcome Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedOutcomeIndex(0)}
                className={`flex-1 py-4 px-4 rounded-[5px] font-inter-tight font-bold text-sm transition-colors ${
                  selectedOutcomeIndex === 0
                    ? quickTradeMode === "buy"
                      ? "bg-[#359a5e] hover:bg-[#2d8450] text-white"
                      : "bg-[#cb3131] hover:bg-[#b82a2a] text-white"
                    : "bg-gray-100 dark:bg-[#242b32] text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{outcome1LabelDisplay}</span>
                  <span className={`font-mono tabular-nums min-w-[3.5em] text-right inline-block ${selectedOutcomeIndex === 0 ? "text-white/90" : "text-gray-500 dark:text-white/50"}`}>
                    <AnimatedPrice value={outcome1Price * 100} format={(v) => `${fmtCents(v / 100, marketData.decimalPrecision)}¢`} />
                  </span>
                </div>
              </button>
              <button
                onClick={() => setSelectedOutcomeIndex(1)}
                className={`flex-1 py-4 px-4 rounded-[5px] font-inter-tight font-bold text-sm transition-colors ${
                  selectedOutcomeIndex === 1
                    ? quickTradeMode === "buy"
                      ? "bg-[#cb3131] hover:bg-[#b82a2a] text-white"
                      : "bg-[#359a5e] hover:bg-[#2d8450] text-white"
                    : "bg-gray-100 dark:bg-[#242b32] text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{outcome2LabelDisplay}</span>
                  <span className={`font-mono tabular-nums min-w-[3.5em] text-right inline-block ${selectedOutcomeIndex === 1 ? "text-white/90" : "text-gray-500 dark:text-white/50"}`}>
                    <AnimatedPrice value={outcome2Price * 100} format={(v) => `${fmtCents(v / 100, marketData.decimalPrecision)}¢`} />
                  </span>
                </div>
              </button>
            </div>

            {/* Limit Order Fields */}
            {orderType === "limit" && (() => {
              // Derive live midpoint once, reuse for: price hint under the
              // input + distance-to-mid badge + rewards guidance. YES and NO
              // prices are complementary so best_bid_this = 1 - best_ask_other.
              const rawAskThis = selectedOutcomeIndex === 0 ? buyPrices.buyYes : buyPrices.buyNo;
              const rawAskOther = selectedOutcomeIndex === 0 ? buyPrices.buyNo : buyPrices.buyYes;
              const midReady =
                Number.isFinite(rawAskThis) && (rawAskThis as number) > 0 &&
                Number.isFinite(rawAskOther) && (rawAskOther as number) > 0;
              const midCents = midReady
                ? ((rawAskThis as number) + Math.max(0, 1 - (rawAskOther as number))) / 2 * 100
                : null;
              const typedCents = parseFloat(limitPrice);
              const hasTyped = Number.isFinite(typedCents);
              const distanceToMid = hasTyped && midCents !== null
                ? Math.abs(typedCents - midCents)
                : null;
              // Expiration presets — mirror Polymarket's ticket.
              const expirationPresets: Array<{ id: typeof expirationMode; label: string }> = [
                { id: "none",   label: "No" },
                { id: "5m",     label: "5m" },
                { id: "1h",     label: "1h" },
                { id: "12h",    label: "12h" },
                { id: "24h",    label: "24h" },
                { id: "eod",    label: "EOD" },
                { id: "custom", label: "Custom" },
              ];
              return (
              <div className="space-y-3">
                {/* Limit Price Input + live midpoint hint */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-inter-tight font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Limit Price
                    </label>
                    {midCents !== null && (
                      <button
                        type="button"
                        onClick={() => setLimitPrice(midCents.toFixed(1))}
                        className="text-[10px] font-mono text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                        title={t("action.snapMidpoint")}
                      >
                        Mid {midCents.toFixed(1)}¢
                        {distanceToMid !== null && hasTyped && (
                          <span className="ml-1 text-gray-400 dark:text-gray-500">
                            · {distanceToMid > 0 ? `±${distanceToMid.toFixed(1)}¢` : "at mid"}
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      min="0.01"
                      max="99.99"
                      step="0.01"
                      value={limitPrice}
                      onChange={(e) => setLimitPrice(e.target.value)}
                      placeholder={fmtCents(currentPrice, marketData.decimalPrecision)}
                      className="w-full px-3 py-2.5 pr-8 text-sm border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] focus:border-gray-400 dark:focus:border-gray-500 focus:ring-0 outline-none bg-white dark:bg-[#0a0a0b] text-gray-900 dark:text-white placeholder-gray-400 font-mono"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">¢</span>
                  </div>
                </div>

                {/* Expiration — Polymarket-style preset pills + optional
                    datetime picker for "Custom". No/5m/1h/12h/24h/EOD/Custom.
                    The concrete epoch is resolved at submit (computeExpiresIn)
                    so EOD doesn't drift if the panel sits open. */}
                <div>
                  <label className="block text-xs font-inter-tight font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    Expiration
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {expirationPresets.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setExpirationMode(p.id)}
                        className={`px-2.5 py-1.5 rounded-[5px] text-[11px] font-inter-tight font-medium transition-colors ${
                          expirationMode === p.id
                            ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                            : "bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {expirationMode === "custom" && (
                    <input
                      type="datetime-local"
                      value={customExpirationDate}
                      onChange={(e) => setCustomExpirationDate(e.target.value)}
                      min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                      className="mt-2 w-full px-3 py-2 text-sm border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] focus:border-gray-400 dark:focus:border-gray-500 focus:ring-0 outline-none bg-white dark:bg-[#0a0a0b] text-gray-900 dark:text-white font-mono"
                    />
                  )}
                </div>

                {/* Post-only toggle — maker-only order. Qualifies for Polymarket
                    liquidity rewards + maker rebates. CLOB rejects if the order
                    would immediately cross (instead of partially filling as taker). */}
                <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={postOnly}
                    onChange={(e) => setPostOnly(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-[#1A1A1A] text-gray-900 dark:bg-[#0a0a0b] focus:ring-0"
                  />
                  <span className="text-gray-700 dark:text-gray-300">
                    <span className="font-medium">Post-only (maker)</span>
                    <span className="block text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                      Reject if it would fill immediately. Makes you eligible for Polymarket liquidity rewards + rebates.
                    </span>
                  </span>
                </label>

                {/* Rewards guidance — only shown when post-only is on and the
                    market actually has a rewards pool. Reuses the hoisted
                    `midCents` from the Limit Price hint so both surfaces
                    reference the same midpoint. Shows qualifying band +
                    min size + a live reward/day upper-bound estimate. */}
                {postOnly && (() => {
                  if (!marketIsRewarded) {
                    return (
                      <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0a0a0b]/50 px-2.5 py-2 text-[11px] text-gray-600 dark:text-gray-400">
                        This market has no liquidity rewards pool today. Post-only still works but won't earn rewards.
                      </div>
                    );
                  }
                  if (!midReady || midCents === null) {
                    return (
                      <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0a0a0b]/50 px-2.5 py-2 text-[11px] text-gray-600 dark:text-gray-400">
                        Waiting for market price to compute the rewards band…
                      </div>
                    );
                  }
                  const halfBandCents = Number.isFinite(rewardsParams!.maxSpread) ? rewardsParams!.maxSpread : 0;
                  const lowCents = Math.max(0.1, midCents - halfBandCents);
                  const highCents = Math.min(99.9, midCents + halfBandCents);
                  const priceInBand = hasTyped && typedCents >= lowCents && typedCents <= highCents;
                  // sizeShares respects amountMode: shares mode → amount IS shares.
                  const sizeShares = quickTradeMode === "buy"
                    ? (amountMode === "shares"
                        ? amount
                        : (hasTyped ? amount / (typedCents / 100) : 0))
                    : amount;
                  const sizeOk = sizeShares >= rewardsParams!.minSize;
                  const snapSide = quickTradeMode === "buy" ? lowCents : highCents;
                  const qualifies = hasTyped && priceInBand && sizeOk;
                  // Upper-bound reward/day estimate (sole-maker scenario):
                  //   score = (1 - distance/maxSpread) * size
                  // Polymarket splits the pool across all qualifying makers;
                  // we can't know the denominator so this is the ceiling.
                  const scoreFactor = qualifies && halfBandCents > 0
                    ? Math.max(0, 1 - (distanceToMid ?? 0) / halfBandCents)
                    : 0;
                  const estRewardPerDay = qualifies
                    ? scoreFactor * rewardsParams!.ratePerDay
                    : 0;
                  return (
                    <div className={`rounded-[5px] border px-2.5 py-2 text-[11px] ${
                      qualifies
                        ? "border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-300"
                        : "border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-300"
                    }`}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-semibold">
                          {qualifies ? "✓ Qualifies for rewards" : "Won't earn rewards yet"}
                        </span>
                        <span className="text-[10px] opacity-75">
                          Pool: ${rewardsParams!.ratePerDay.toFixed(2)}/day
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 leading-tight">
                        <div>
                          Price band: <span className="font-mono">{lowCents.toFixed(1)}¢ – {highCents.toFixed(1)}¢</span>
                          {hasTyped && !priceInBand && <span className="block text-[10px] opacity-75">Your price: {typedCents.toFixed(1)}¢</span>}
                        </div>
                        <div>
                          Min size: <span className="font-mono">{rewardsParams!.minSize}</span> shares
                          {hasTyped && !sizeOk && <span className="block text-[10px] opacity-75">Your size: {sizeShares.toFixed(2)}</span>}
                        </div>
                      </div>
                      {/* Live reward/day estimate — only meaningful when
                          qualifying. "If alone" is the upper bound; real
                          payout is that × your_score / total_score. */}
                      {qualifies && estRewardPerDay > 0 && (
                        <div className="mt-1.5 text-[11px]">
                          Est. up to{" "}
                          <span className="font-mono font-semibold">${estRewardPerDay.toFixed(2)}/day</span>
                          <span className="opacity-75"> if sole maker at this price</span>
                        </div>
                      )}
                      <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                        {hasTyped && !priceInBand && (
                          <button
                            type="button"
                            onClick={() => setLimitPrice(snapSide.toFixed(1))}
                            className="text-[11px] font-medium underline underline-offset-2 hover:opacity-80"
                          >
                            Snap price to {snapSide.toFixed(1)}¢
                          </button>
                        )}
                        {/* Snap-to-minSize respects amountMode. In shares
                            mode just jump to minSize directly; in USD mode
                            convert minSize × price. */}
                        {hasTyped && !sizeOk && (
                          <button
                            type="button"
                            onClick={() => {
                              const min = rewardsParams!.minSize;
                              const target = quickTradeMode === "buy"
                                ? (amountMode === "shares"
                                    ? min
                                    : Math.ceil(min * (typedCents / 100) * 100) / 100)
                                : min;
                              setAmount(target);
                              setAmountInput(String(target));
                            }}
                            className="text-[11px] font-medium underline underline-offset-2 hover:opacity-80"
                          >
                            Snap size to {rewardsParams!.minSize} shares
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
              );
            })()}

            {/* Split/Merge UI */}
            {orderType === "split-merge" && (
              <div className="space-y-3">
                <p className="text-[11px] text-gray-400 dark:text-gray-500">
                  Split converts USDC into YES + NO shares. Merge converts equal YES + NO back to USDC.
                </p>
                <div>
                  <label className="block text-xs font-inter-tight font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    Amount
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={splitMergeAmount}
                      onChange={(e) => setSplitMergeAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2.5 pr-16 text-sm border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] focus:border-gray-400 dark:focus:border-gray-500 focus:ring-0 outline-none bg-white dark:bg-[#0a0a0b] text-gray-900 dark:text-white placeholder-gray-400 font-mono"
                    />
                    {maxMergeAmount > 0 && (
                      <button
                        onClick={() => setSplitMergeAmount(String(maxMergeAmount))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-[#18181D]"
                      >
                        MAX
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => executeSplitMerge("split")}
                    disabled={splitMergeLoading || !splitMergeAmount || parseFloat(splitMergeAmount) <= 0}
                    className="py-2.5 text-xs font-inter-tight font-bold rounded-[5px] bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {splitMergeLoading ? "..." : "Split → YES + NO"}
                  </button>
                  <button
                    onClick={() => executeSplitMerge("merge")}
                    disabled={splitMergeLoading || !hasBothOutcomes || !splitMergeAmount || parseFloat(splitMergeAmount) <= 0}
                    className="py-2.5 text-xs font-inter-tight font-bold rounded-[5px] bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-500/30 hover:bg-purple-100 dark:hover:bg-purple-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {splitMergeLoading ? "..." : "Merge → USDC"}
                  </button>
                </div>
                {!hasBothOutcomes && userPositions.length > 0 && (
                  <p className="text-[10px] text-gray-400 text-center">Need both YES and NO positions to merge</p>
                )}
                {splitMergeSuccess && (
                  <p className="text-[11px] text-center text-emerald-500 font-medium">{splitMergeSuccess}</p>
                )}
                {splitMergeError && (
                  <p className="text-[11px] text-center text-red-500">{splitMergeError}</p>
                )}
              </div>
            )}

            {/* Wallet + Balance — shown for all sources in ninja mode */}
            {isLoggedIn && (displayWallet || safeDisplayBalance !== null) && (
              <div className="flex items-center justify-between px-1 py-2 border-b border-gray-200 dark:border-[#1A1A1A]/50 mb-1">
                {displayWallet && (
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-mono text-gray-400">{displayWallet.slice(0, 6)}...{displayWallet.slice(-4)}</span>
                    <button onClick={copyWallet} className="p-0.5 hover:bg-gray-100 dark:hover:bg-white/5 rounded transition-colors">
                      {walletCopied ? <FiCheck className="w-3 h-3 text-emerald-500" /> : <FiCopy className="w-3 h-3 text-gray-400" />}
                    </button>
                  </div>
                )}
                {safeDisplayBalance !== null && (
                  <div className="flex items-center gap-1.5 ml-auto">
                    <span className={`text-xs font-inter-tight font-semibold tabular-nums ${insufficientBalance ? 'text-[#FF1744]' : 'text-gray-700 dark:text-gray-300'}`}>
                      ${safeDisplayBalance.toFixed(2)}
                    </span>
                    <CdnImage
                      src={source === 'predict' ? '/usdt.png' : '/usdc.png'}
                      alt={balanceSymbol}
                      width={14}
                      height={14}
                      className="rounded-[5px] flex-shrink-0"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Amount, Returns, Execute — hidden in split-merge mode */}
            {orderType !== "split-merge" && <>
            <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-inter-tight font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {isSellMode ? "Shares" : isSharesInput ? "Shares" : "Amount"}
                  </span>
                  {/* USD ⇌ Shares toggle — buy-limit only (market orders are
                      always USD, sell is always shares). Mirrors Polymarket
                      where the maker can think in either dimension. */}
                  {!isSellMode && orderType === "limit" && (
                    <div className="inline-flex rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setAmountMode("usd")}
                        className={`px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                          amountMode === "usd"
                            ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                            : "bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        }`}
                      >
                        USD
                      </button>
                      <button
                        type="button"
                        onClick={() => setAmountMode("shares")}
                        className={`px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                          amountMode === "shares"
                            ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                            : "bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        }`}
                      >
                        Shares
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center">
                  {!isSellMode && !isSharesInput && (
                    <span className="font-mono text-2xl font-medium text-gray-400 dark:text-gray-500">$</span>
                  )}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amountInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Allow only numbers and one decimal point
                      if (!/^\d*\.?\d*$/.test(raw)) return;
                      setAmountInput(raw);
                      const val = parseFloat(raw);
                      // Cap at 100,000 to prevent unreasonable values (USD in buy mode, shares in sell mode)
                      const MAX_AMOUNT = 100000;
                      setAmount(isNaN(val) ? 0 : Math.min(Math.max(0, val), MAX_AMOUNT));
                    }}
                    onBlur={() => {
                      // Format on blur: remove leading zeros, ensure valid number
                      const val = parseFloat(amountInput);
                      if (isNaN(val) || val === 0) {
                        setAmountInput("0");
                        setAmount(0);
                      } else {
                        // Format with up to 2 decimals, remove trailing zeros
                        setAmountInput(String(Math.round(val * 100) / 100));
                      }
                    }}
                    style={{ width: `${Math.max(3, amountInput.length + 0.5)}ch` }}
                    className="font-mono text-2xl font-semibold bg-transparent border-none outline-none text-right min-w-[3ch] max-w-full text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Sell mode: show owned shares so the user knows what's available */}
              {isSellMode && (
                <div className="flex items-center justify-between mb-2">
                  {ownedShares > 0 ? (
                    <span className={`px-2 py-1 rounded-[5px] text-xs font-semibold ${
                      selectedOutcomeIndex === 0
                        ? 'bg-[#00FF9D]/15 text-[#00FF9D]'
                        : 'bg-[#FF1744]/15 text-[#FF1744]'
                    }`}>
                      {ownedShares.toFixed(2)} {selectedOutcomeLabelDisplay}
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-[5px] text-xs font-semibold bg-gray-100 dark:bg-[#1A1A1A] text-gray-500">
                      No position
                    </span>
                  )}
                </div>
              )}

              {/* Quick Add Buttons — dollars in buy mode, % of position in sell mode,
                  shares deltas in buy-limit shares mode. */}
              {isSharesInput ? (
                <div className="flex gap-1.5">
                  {[-100, -10, +10, +100, +200].map((delta) => (
                    <button
                      key={delta}
                      type="button"
                      onClick={() => {
                        const next = Math.max(0, Math.round((amount + delta) * 100) / 100);
                        setAmount(next);
                        setAmountInput(String(next));
                      }}
                      className="flex-1 py-2.5 px-1 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-300 text-xs font-inter-tight font-medium hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      {delta > 0 ? `+${delta}` : String(delta)}
                    </button>
                  ))}
                </div>
              ) : isSellMode ? (
                <div className="flex gap-2">
                  {[25, 50, 75, 100].map((pct) => {
                    const shareAmount = Math.floor(ownedShares * pct / 100 * 100) / 100;
                    return (
                      <button
                        key={pct}
                        onClick={() => {
                          setAmount(shareAmount);
                          setAmountInput(String(shareAmount));
                          setCustomSellPct("");
                        }}
                        disabled={ownedShares <= 0}
                        className="flex-1 py-2.5 px-2 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-300 text-xs font-inter-tight font-medium hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {pct}%
                      </button>
                    );
                  })}
                  {/* Custom % input — one-off, not persisted */}
                  <div className={`flex-1 flex items-center justify-center rounded-[5px] border text-xs font-inter-tight font-medium transition-colors ${
                    ownedShares <= 0
                      ? "bg-white dark:bg-[#18181D] border-gray-200 dark:border-[#1A1A1A] opacity-40 cursor-not-allowed"
                      : customSellPct
                        ? "bg-white dark:bg-[#18181D] border-gray-400 dark:border-gray-500"
                        : "bg-white dark:bg-[#18181D] border-gray-200 dark:border-[#1A1A1A] hover:border-gray-400 dark:hover:border-gray-500"
                  }`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={customSellPct}
                      placeholder="—"
                      disabled={ownedShares <= 0}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (!/^\d*\.?\d*$/.test(raw)) return;
                        setCustomSellPct(raw);
                        const pct = parseFloat(raw);
                        if (!isNaN(pct) && pct > 0) {
                          const clamped = Math.min(pct, 100);
                          const shareAmount = Math.floor(ownedShares * clamped / 100 * 100) / 100;
                          setAmount(shareAmount);
                          setAmountInput(String(shareAmount));
                        }
                      }}
                      className="w-full py-2.5 px-2 bg-transparent text-center text-gray-700 dark:text-gray-300 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-600 disabled:cursor-not-allowed"
                    />
                    {customSellPct && <span className="pr-2 text-gray-500 dark:text-gray-400">%</span>}
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  {[1, 10, 50].map((add) => (
                    <button
                      key={add}
                      onClick={() => addAmount(add)}
                      className="flex-1 py-2.5 px-4 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-300 text-xs font-inter-tight font-medium hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center justify-center gap-1"
                    >
                      <FiPlus className="w-3 h-3" />
                      ${add}
                    </button>
                  ))}
                  <button
                    onClick={() => addAmount(-1)}
                    className="flex-1 py-2.5 px-4 rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-300 text-xs font-inter-tight font-medium hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    Max
                  </button>
                </div>
              )}
              {/* Dollar quick-buy presets — buy-USD mode only (hidden in shares mode) */}
              {!isSellMode && !isSharesInput && (
                userMeLoading && !userMe ? (
                  // Skeleton: userMe is still loading and we have no cache to
                  // seed from — show placeholder preset buttons so the panel
                  // doesn't flash "Set up quick presets" before the real ones.
                  <div className="flex gap-2 items-center mt-2 animate-pulse">
                    <div className="flex-1 h-[34px] rounded-[5px] bg-gray-100 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A]" />
                    <div className="flex-1 h-[34px] rounded-[5px] bg-gray-100 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A]" />
                    <div className="flex-1 h-[34px] rounded-[5px] bg-gray-100 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A]" />
                  </div>
                ) : quickBuyAmounts.length > 0 ? (
                  <div className="flex gap-2 items-center mt-2">
                    {quickBuyAmounts.map((preset) => (
                      <button
                        key={preset}
                        onClick={() => setAmountInput(String(preset))}
                        className={`flex-1 py-2 px-3 rounded-[5px] text-xs font-inter-tight font-medium transition-colors ${
                          amountInput === String(preset)
                            ? "bg-gray-900/5 dark:bg-white/5 border border-gray-400 dark:border-gray-500 text-gray-900 dark:text-white"
                            : "bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-white"
                        }`}
                      >
                        ${preset}
                      </button>
                    ))}
                    <a href="/settings?tab=trading" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-sm flex-shrink-0">✏️</a>
                  </div>
                ) : (
                  <a href="/settings?tab=trading" className="block text-center text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors py-1">
                    Set up quick presets →
                  </a>
                )
              )}
            </div>

            {/* Returns Preview — buy mode shows potential winnings,
                sell mode shows USDC proceeds from the sale. Shares-input
                mode surfaces the USD cost next to share count so the user
                knows the notional before submitting. */}
            <div className="flex items-center justify-between px-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                <span className="font-mono">{shares.toFixed(2)}</span> shares @ <span className="font-mono">{fmtCents(effectivePrice, marketData.decimalPrecision)}¢</span>
                {isSharesInput && (
                  <> · cost <span className="font-mono">${notionalUsd.toFixed(2)}</span></>
                )}
              </span>
              <span className={`font-inter-tight text-lg font-semibold ${
                potentialProfit > 0 ? "text-[#00FF9D]" : "text-gray-500"
              }`}>
                {isSellMode ? "" : "+"}${potentialProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {/* Slippage warning — renders ONLY when the user has bumped
                their `default_slippage_pct` above 10% (default is 5%, so
                246/251 users in prod never see this). Surfaces the price
                ceiling the CLOB is allowed to walk to, since the BUY/SELL
                button only shows the top-of-book ask. Limit orders bypass
                this entirely (the user-typed limit IS the ceiling). */}
            {orderType !== "limit" && (
              <SlippageWarning
                slippagePct={(userMe as { default_slippage_pct?: number } | null)?.default_slippage_pct}
                currentPrice={currentPrice}
                side={quickTradeMode === "sell" ? "sell" : "buy"}
              />
            )}

            {/* Execute Button */}
            <button
              ref={executeButtonRef}
              onClick={() => {
                // "Add $X & Buy OUTCOME" state — balance is short, so the
                // click's intent isn't really to trade; it's to add funds.
                // Route to the source-correct deposit flow instead of
                // firing executeTrade (which would just error out).
                if (insufficientBalance && quickTradeMode === "buy") {
                  if (source === 'predict') {
                    openBscDeposit();
                  } else if (source === 'kalshi') {
                    if (solanaAddress) {
                      fundSolanaWallet({ address: solanaAddress, options: { asset: 'USDC' } }).catch(() => {});
                    }
                  } else {
                    openPolymarketDeposit();
                  }
                  return;
                }
                executeTrade();
              }}
              disabled={isLoading || shares <= 0 || (isSellMode && shares > ownedShares) || kalshiTradingUnavailable}
              className={`w-full py-4 rounded-[5px] font-inter-tight font-bold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                insufficientBalance && quickTradeMode === "buy"
                  ? "bg-white dark:bg-white text-black border-2 border-gray-300 dark:border-gray-600"
                  : quickTradeMode === "sell"
                    ? "bg-[#FF1744] hover:bg-[#FF1744]/90 text-white"
                    : selectedOutcomeIndex === 0
                      ? "bg-[#00FF9D] hover:bg-[#00FF9D]/90 text-black"
                      : "bg-[#FF1744] hover:bg-[#FF1744]/90 text-white"
              }`}
            >
              {!isLoggedIn ? (
                "Sign in to trade"
              ) : isLoading ? (
                <>
                  <FiLoader className="w-5 h-5 animate-spin" />
                  Trading...
                </>
              ) : showSuccess ? (
                <>
                  <FiCheck className="w-5 h-5" strokeWidth={3} />
                  Success!
                </>
              ) : source === 'polymarket' && enablingTrading ? (
                <>
                  <FiLoader className="w-5 h-5 animate-spin" />
                  Enabling...
                </>
              ) : source === 'polymarket' && isCheckingBackend ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" />
                  Loading...
                </>
              ) : source === 'polymarket' && !tradingEnabled ? (
                "Enable trading"
              ) : kalshiTradingUnavailable ? (
                kalshiTradeDisabledReason || "Kalshi unavailable"
              ) : insufficientBalance && quickTradeMode === "buy" ? (
                `Add $${Math.max(0, notionalUsd - (safeDisplayBalance || 0)).toFixed(2)} & Buy ${selectedOutcomeLabelDisplay}`
              ) : (
                quickTradeMode === "sell"
                  ? `Sell ${selectedOutcomeLabelDisplay}`
                  : orderType === "limit" ? `Limit Buy ${selectedOutcomeLabelDisplay}` : `Buy ${selectedOutcomeLabelDisplay}`
              )}
            </button>
            </>}
          </div>
        ) : marketEnded ? (
          /* Market Closed — Simplified centered view */
          <div className="py-8 flex flex-col items-center justify-center text-center">
            {(() => {
              // Determine winning outcome: price >= 0.95 means Yes won, <= 0.05 means No won
              const yesPrice = buyPrices.buyYes ?? 0;
              const isYesWinner = yesPrice >= 0.95;
              const winningOutcome = isYesWinner
                ? (marketData.outcomes?.[0] || 'Yes')
                : (marketData.outcomes?.[1] || 'No');
              const outcomeTitle = marketData.groupItemTitle || marketData.question;
              return (
                <>
                  <span className="text-xs font-inter-tight text-gray-500 dark:text-gray-400 mb-1">Outcome:</span>
                  <span className={`text-2xl font-monument font-bold ${
                    isYesWinner ? 'text-[#00FF9D]' : 'text-[#FF1744]'
                  }`}>
                    {winningOutcome}
                  </span>
                  <p className="font-satoshi font-semibold text-base text-gray-900 dark:text-white mt-2">
                    {outcomeTitle}
                  </p>
                </>
              );
            })()}
          </div>
        ) : (
          /* Normal Mode - Dynamic Outcome Buttons */
          <div ref={inlineButtonsRef} className="flex flex-col md:flex-row gap-3">
            {marketData.outcomes.map((outcome, index) => {
              // Use buyPrices for consistency with Ninja mode and other components
              const price = index === 0 ? buyPrices.buyYes : buyPrices.buyNo;
              const label = localizeYesNo(outcome, (outcome ?? (index === 0 ? "Yes" : "No")).toUpperCase());
              // Alternate colors: green for first/odd outcomes, red for second/even
              const isGreen = index % 2 === 0;

              return (
                <button
                  key={outcome}
                  onClick={() => handleOutcomeClick(index as 0 | 1)}
                  className={`flex-1 rounded-[5px] p-4 transition-colors ${
                    isGreen
                      ? "bg-[#359a5e]/15 hover:bg-[#359a5e]/25"
                      : "bg-[#cb3131]/15 hover:bg-[#cb3131]/25"
                  }`}
                >
                  <div>
                    <div className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white text-left">
                      Buy {label}
                    </div>
                    <div className={`font-monument text-2xl text-left tabular-nums transition-colors duration-500 ${
                      isGreen ? "text-[#3db468]" : "text-[#cb3131]"
                    } ${
                      (index === 0 ? flash1 : flash2) === 'up' ? '!text-[#3db468] brightness-125' :
                      (index === 0 ? flash1 : flash2) === 'down' ? '!text-[#cb3131] brightness-125' : ''
                    }`}>
                      <AnimatedPrice value={price * 100} format={(v) => `${fmtCents(v / 100, marketData.decimalPrecision)}¢`} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Trading Modal - Polymarket only */}
      {source === 'polymarket' && (
        <TradingModal
          isOpen={tradingModal.isOpen}
          onClose={() => setTradingModal({ ...tradingModal, isOpen: false })}
          marketSlug={marketData.slug}
          outcome={tradingModal.outcome}
          actualOutcome={tradingModal.actualOutcome}
          outcomePrice={(tradingModal.outcomeIndex ?? 0) === 0 ? buyPrices.buyYes : buyPrices.buyNo}
          altOutcomePrice={(tradingModal.outcomeIndex ?? 0) === 0 ? buyPrices.buyNo : buyPrices.buyYes}
          altActualOutcome={(tradingModal.outcomeIndex ?? 0) === 0 ? outcome2Value : outcome1Value}
          // Pass the full outcomes pair so the modal renders real labels
          // (Over/Under, Up/Down) instead of falling back to "Yes"/"No".
          outcomes={
            outcome1Value && outcome2Value
              ? [outcome1Value, outcome2Value]
              : undefined
          }
          marketQuestion={marketData.question}
          marketImage={marketData.image}
          quickBuyAmounts={quickBuyAmounts}
          clobTokenIds={marketData.clobTokenIds}
        />
      )}
    </>
  );
}
